<template>
  <ion-page>
    <ion-content class="h-full" v-if="category && task">
      <round-header
        :title="task.name"
        subtitle="Task"
        v-on:pickIcon="setIcon"
        :database="icons"
        @input="setName($event)"
        :image="task.icon"
        back="/home"
        image-type="icon"
        :edit-mode="true"
        v-on:toggleEditMode="$router.back()"
      >
        <div
          class="font-outline-1 font-fredoka relative z-10 text-center text-xl text-gray-100 drop-shadow"
        >
          Score
        </div>
        <div class="flex justify-center">
          <TinyButton
            :class="task.score <= 0 ? 'opacity-25' : ''"
            :src="ICON_MINUS"
            v-on:click="addToScore(-1)"
          />
          <Fill
            :hide-lines="Math.abs(task.score) < 1"
            class="h-8 w-64"
            :value="0"
            :max="Math.abs(task.score)"
            :label="task.score"
          />
          <TinyButton :src="ICON_PLUS" v-on:click="addToScore(1)" />
        </div>
      </round-header>
      <OptionStepper
        :label="'Cooldown: ' + (task.cooldown > 0 ? 'Enabled' : 'Disabled')"
        v-on:increase="toggleCooldown"
        v-on:decrease="toggleCooldown"
      />
      <OptionStepper
        :label="task.isCooldownReversed ? 'Reversed Cooldown' : 'Normal Cooldown'"
        v-on:increase="toggleCooldownReversed"
        v-on:decrease="toggleCooldownReversed"
      />
      <template v-for="unit in timeUnits" :key="unit.seconds">
        <OptionStepper
          v-if="task.cooldown > 0"
          :label="
            (Math.floor(task.cooldown / unit.seconds) % unit.perNextUnit) +
            ' ' +
            (Math.floor(task.cooldown / unit.seconds) % unit.perNextUnit == 1
              ? unit.singular
              : unit.plural)
          "
          v-on:increase="addToCooldown(unit.seconds)"
          v-on:decrease="addToCooldown(-unit.seconds)"
        />
      </template>

      <OptionStepper
        :label="task.expiresIn > 0 ? `Limited to ${task.expiresIn} use(s)` : 'No Limit'"
        v-on:increase="addToExpiration(1)"
        v-on:decrease="addToExpiration(-1)"
      />
      <OptionStepper
        :label="'Sharing: ' + (task.public ? 'Enabled' : 'Disabled')"
        v-on:increase="toggleSharing"
        v-on:decrease="toggleSharing"
      />
      <OptionStepper
        :label="'Photo Mode: ' + (task.photoMode ? 'Enabled' : 'Disabled')"
        v-on:increase="togglePhotoMode"
        v-on:decrease="togglePhotoMode"
      />
      <div class="flex justify-center">
        <div
          class="bg-bg-neutral mt-2 w-[512px] max-w-[90%] rounded-2xl p-4 text-center"
          v-if="task.public"
        >
          <div class="font-fredoka text-bg-sidebar text-lg">Task ID:</div>

          <div class="mt-1 flex items-center justify-center gap-2">
            <div class="font-fredoka font-outline-1 text-2xl break-all text-white">
              {{ userId }}/{{ taskId }}
            </div>

            <!-- 📋 Copy Button -->
            <button
              @click="copyUid"
              class="hover:text-bg-sidebar text-white transition"
              title="Copy to clipboard"
            >
              <Copy :size="20" />
            </button>
          </div>

          <div v-if="copied" class="text-bg-sidebar mt-1 text-xs">Copied!</div>
        </div>
      </div>

      <!-- Offline mode indicator for task editing -->
      <div v-if="!isOnline" class="flex justify-center mt-4 mb-8">
        <div class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 max-w-[90%] rounded-md flex items-start">
          <WifiOff class="mr-2 flex-shrink-0 h-5 w-5" />
          <div>
            <p class="font-medium">You're currently offline</p>
            <p class="text-sm">Changes to this task will be saved locally and synced when your connection is restored.</p>
          </div>
        </div>
      </div>

      <div class="flex justify-center">
        <button
          class="text-delete font-fredoka absolute bottom-2 cursor-pointer rounded-xl bg-pink-800 px-4 py-2 text-lg hover:bg-pink-600 active:bg-pink-200 flex items-center"
          v-on:click="deleteTask"
        >
          Delete Task
          <WifiOff v-if="!isOnline" class="ml-2 h-4 w-4" />
        </button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts" setup>
import { IonContent, IonPage } from '@ionic/vue';
import { useRoute } from 'vue-router';
import { saveAndSync, usePreferencesItem } from '../composable/usePreferenceCollection.ts';
import { Category } from '../models/Category.ts';
import { ICON_MINUS, ICON_PLUS, icons } from '../services/Directus.ts';
import Fill from '../components/Fill.vue';
import RoundHeader from '../components/RoundHeader.vue';
import TinyButton from '../components/TinyButton.vue';
import OptionStepper from '../components/OptionStepper.vue';
import router from '../router';
import { doc, getFirestore } from 'firebase/firestore';
import { useDocument } from 'vuefire';
import { userId } from '../services/FirebaseDB.ts';
import { Task } from '../models/Task.ts';
import { Copy, WifiOff } from 'lucide-vue-next';
import { ref } from 'vue';
import { isOnline } from '../composable/useNetworkStatus.ts';

const route = useRoute();
const taskId = route.params.taskId as string;
const category = usePreferencesItem(Category, route.params.categoryId as string);
const copied = ref(false);

const db = getFirestore();
const task = useDocument(() =>
  userId.value ? doc(db, 'users', userId.value, 'tasks', taskId) : null
);

const timeUnits = [
  { seconds: 60 * 60 * 24 * 30 * 12, perNextUnit: 100, singular: 'Year', plural: 'Years' },
  { seconds: 60 * 60 * 24 * 30, perNextUnit: 12, singular: 'Month', plural: 'Months' },
  { seconds: 60 * 60 * 24, perNextUnit: 30, singular: 'Day', plural: 'Days' },
  { seconds: 60 * 60, perNextUnit: 24, singular: 'Hour', plural: 'Hours' },
  { seconds: 60, perNextUnit: 60, singular: 'Minute', plural: 'Minutes' },
  { seconds: 1, perNextUnit: 60, singular: 'Second', plural: 'Seconds' },
];

async function addToScore(value: number) {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.score = Math.max(0, task.value.score + value);
  await Task.fromPlain(task.value).save(db);
}
async function addToExpiration(value: number) {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.expiresIn = Math.max(0, (task.value.expiresIn ?? 0) + value);
  await Task.fromPlain(task.value).save(db);
}
async function addToCooldown(value: number) {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  const newValue = task.value.cooldown + value;
  if (newValue < 0) {
    return;
  }
  task.value.cooldown = newValue;
  await Task.fromPlain(task.value).save(db);
}

async function setIcon(id: string) {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.icon = id;
  await Task.fromPlain(task.value).save(db);
}

async function toggleCooldown() {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.cooldown = task.value.cooldown > 0 ? 0 : 60 * 60;
  await Task.fromPlain(task.value).save(db);
}
async function toggleSharing() {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.public = !task.value.public;
  await Task.fromPlain(task.value).save(db);
}

async function togglePhotoMode() {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.photoMode = !task.value.photoMode;
  await Task.fromPlain(task.value).save(db);
}

async function toggleCooldownReversed() {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.isCooldownReversed = !task.value.isCooldownReversed;
  await Task.fromPlain(task.value).save(db);
}

async function setName(name: string) {
  if (category.value == undefined || task.value == undefined) {
    return;
  }
  task.value.name = name;
  await Task.fromPlain(task.value).save(db);
}

async function deleteTask() {
  if (category.value == undefined || task.value == null) {
    return;
  }
  const id = task.value.id;
  category.value.taskIds = category.value.taskIds.filter((e) => e != id);
  await Task.fromPlain(task.value).delete(db);
  await saveAndSync(Category, category.value);
  router.push('/category/' + category.value.id);
}

const copyUid = async () => {
  if (task.value) {
    await navigator.clipboard.writeText(`${userId.value}/${task.value.id}`);
    copied.value = true;

    setTimeout(() => (copied.value = false), 1500);
  }
};
</script>
